import { tailwindOverride, Tooltip } from '@getsynapse/design-system';
import { FC } from 'react';
import { StatusOption } from './StatusTableRowCell';

type OptionsProps = {
  selected: boolean;
  otherProps: Record<string, any>;
  selectOption: () => void;
  option: StatusOption;
  isHighlighted?: boolean;
};

export const Options: FC<OptionsProps> = (props: OptionsProps) => {
  const {
    selected,
    otherProps,
    selectOption,
    option,
    isHighlighted = false,
  } = props;

  const disabled = !!option.disabled;
  const disabledClass = disabled ? 'text-neutral-lighter cursor-pointer' : '';

  const moveFocusBetweenOptions = (
    current: HTMLElement,
    direction: 'up' | 'down'
  ) => {
    const root = current.closest(
      '[data-dropdown-root="true"]'
    ) as HTMLElement | null;
    if (!root) return;

    const allNodes = Array.from(
      root.querySelectorAll<HTMLElement>('[data-status-option="true"]')
    );

    const enabledNodes = allNodes.filter(
      (node) => node.dataset.disabled !== 'true'
    );
    if (!enabledNodes.length) return;

    const currentIndex = enabledNodes.findIndex((n) => n === current);
    if (currentIndex === -1) return;

    const nextIndex =
      direction === 'down' ? currentIndex + 1 : currentIndex - 1;

    if (nextIndex < 0 || nextIndex >= enabledNodes.length) return;

    enabledNodes[nextIndex].focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    const { key } = e;

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      moveFocusBetweenOptions(
        e.currentTarget,
        key === 'ArrowDown' ? 'down' : 'up'
      );
      return;
    }

    if (key === 'Enter' || key === ' ') {
      e.preventDefault();
      e.stopPropagation();

      if (!disabled) {
        selectOption();
      }
    }
  };

  const listItem = (
    <li
      {...otherProps}
      data-status-option='true'
      data-disabled={disabled ? 'true' : 'false'}
      onClick={() => {
        if (!disabled) {
          selectOption();
        }
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      role='option'
      aria-selected={selected}
      className={tailwindOverride(
        'font-body text-base truncate group px-4 py-2 focus-visible:border-0 focus-visible:ring-0 focus-visible:outline-none',
        {
          'cursor-pointer text-neutral-black hover:text-neutral-dark hover:shadow-list-item-hover hover:bg-neutral-lighter-two focus-visible:bg-neutral-lighter-two focus-visible:text-neutral-dark focus-visible:shadow-list-item-hover':
            !disabled,
          'text-neutral-white bg-primary hover:text-neutral-white hover:bg-primary focus-visible:bg-primary focus-visible:text-neutral-white':
            selected && !disabled,
          'bg-neutral-lighter-two': isHighlighted && !selected && !disabled,
        },
        disabledClass
      )}
    >
      {option.label}
    </li>
  );

  const tooltip = (
    <Tooltip
      className='w-full'
      showPopper
      usePortal
      openMode='hover1'
      timeout={0}
      trigger={listItem}
      position='topCenter'
      contentProps={{
        className: 'z-200000 px-3 py-2 text-sm',
      }}
    >
      {option.message || ''}
    </Tooltip>
  );

  return disabledClass ? tooltip : listItem;
};
